import './Analytics.scss';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import Skeleton from '../../components/Skeleton/Skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { colors } from '../../styles/tokens';

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [topVenues, setTopVenues] = useState([]);
  const [topSearches, setTopSearches] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [dauData, setDauData] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ top: [], week_start: null, week_end: null });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const navigate = useNavigate();

  const drillDown = (eventType, venueId, venueName) => {
    const params = new URLSearchParams({ days });
    if (eventType) params.set('event_type', eventType);
    if (venueId) params.set('venue_id', venueId);
    if (venueName) params.set('venue_name', venueName);
    navigate(`/analytics/detail?${params}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, venuesRes, searchesRes, funnelRes, dauRes, leaderboardRes] = await Promise.all([
        api.get(`/analytics/overview?days=${days}`),
        api.get(`/analytics/top-venues?days=${days}&limit=5`),
        api.get(`/analytics/top-searches?days=${days}&limit=5`),
        api.get(`/analytics/conversion-funnel?days=${days}`),
        api.get(`/analytics/daily-active-users?days=${days}`),
        api.get('/analytics/admin-leaderboard?limit=10').catch(() => ({ data: { data: { top: [], week_start: null, week_end: null } } })),
      ]);

      setOverview(overviewRes.data.data);
      setTopVenues(venuesRes.data.data);
      setTopSearches(searchesRes.data.data);
      setFunnel(funnelRes.data.data);
      setDauData(dauRes.data.data);
      setLeaderboard(leaderboardRes.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const guestlistRate = ((funnel?.guestlist?.conversion_rate || 0) * 100).toFixed(1);
  const bottleRate = ((funnel?.bottle_service?.conversion_rate || 0) * 100).toFixed(1);

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analytics">

            {/* Header */}
            <div className="analytics__header">
              <div>
                <h1 className="analytics__title">Analytics</h1>
                <p className="analytics__subtitle">How is your app performing?</p>
              </div>
              <div className="analytics__period">
                {[7, 14, 30, 90].map(d => (
                  <button key={d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Skeleton type="card" />
                <Skeleton type="table" />
                <Skeleton type="table" />
              </div>
            ) : (
              <>
                {/* Question 1: Who's using the app? */}
                <div className="analytics__stats">
                  <div className="analytics__stat-card" onClick={() => navigate('/users')} style={{ cursor: 'pointer' }}>
                    <h3>Total Users</h3>
                    <p>{overview?.total_users?.toLocaleString() || 0}</p>
                    <span>All time</span>
                  </div>
                  <div className="analytics__stat-card" onClick={() => drillDown()} style={{ cursor: 'pointer' }}>
                    <h3>Active Users</h3>
                    <p>{overview?.active_users?.toLocaleString() || 0}</p>
                    <span>Last {days} days — tap to see who</span>
                  </div>
                  <div className="analytics__stat-card" onClick={() => navigate(`/analytics/actions-today?days=${days}`)} style={{ cursor: 'pointer' }}>
                    <h3>Total Actions</h3>
                    <p>{overview?.engagements?.toLocaleString() || 0}</p>
                    <span>Taps, views, searches</span>
                  </div>
                  <div className="analytics__stat-card">
                    <h3>Avg. per User</h3>
                    <p>{overview?.active_users ? (overview.engagements / overview.active_users).toFixed(1) : '0'}</p>
                    <span>Actions per active user</span>
                  </div>
                </div>

                {/* Weekly leaderboard — outreach signal for unclaimed venues */}
                <div className="analytics__chart-section" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                    <h2 style={{ margin: 0 }}>Most viewed venues — last week</h2>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                      {leaderboard.week_start && leaderboard.week_end
                        ? `${new Date(leaderboard.week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${new Date(new Date(leaderboard.week_end).getTime() - 86400000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                        : ''}
                    </span>
                  </div>
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Venue</th>
                        <th style={{ width: 90 }}>Views</th>
                        <th style={{ width: 90 }}>Δ vs prior</th>
                        <th style={{ width: 110 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.top.map((v) => {
                        const delta = v.previous_rank == null ? null : v.previous_rank - v.rank;
                        const deltaLabel =
                          v.previous_rank == null ? 'NEW' :
                          delta > 0 ? `↑ ${delta}` :
                          delta < 0 ? `↓ ${Math.abs(delta)}` : '—';
                        const deltaColor =
                          v.previous_rank == null ? '#A89AFF' :
                          delta > 0 ? '#2ECC71' :
                          delta < 0 ? '#E74C3C' : 'rgba(255,255,255,0.4)';
                        return (
                          <tr key={v.venue_id} onClick={() => drillDown('venue_view', v.venue_id, v.venue_name)} style={{ cursor: 'pointer' }}>
                            <td style={{ fontWeight: 700 }}>#{v.rank}</td>
                            <td>{v.venue_name}</td>
                            <td>{v.views}</td>
                            <td style={{ color: deltaColor, fontWeight: 600 }}>{deltaLabel}</td>
                            <td>
                              {v.claimed ? (
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: 'rgba(46,204,113,0.12)', color: '#2ECC71', border: '1px solid rgba(46,204,113,0.25)' }}>Claimed</span>
                              ) : (
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: 'rgba(241,196,15,0.10)', color: '#F1C40F', border: '1px solid rgba(241,196,15,0.25)' }}>Lead</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {leaderboard.top.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', padding: 32 }}>No views last week</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Question 2: Daily trend */}
                <div className="analytics__chart-section">
                  <h2>Daily Active Users</h2>
                  {dauData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={dauData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" tickFormatter={formatDate} stroke="rgba(255,255,255,0.25)" fontSize={11} />
                        <YAxis stroke="rgba(255,255,255,0.25)" fontSize={11} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#111', border: '1px solid #1a1a1a', borderRadius: 10, fontSize: 13 }}
                          labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                        />
                        <Line type="monotone" dataKey="active_users" stroke={colors.brand} strokeWidth={2.5} dot={false} name="Users" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', padding: 60 }}>No data yet</div>
                  )}
                </div>

                {/* Question 3: What's getting attention? */}
                <div className="analytics__tables">
                  <div className="analytics__table">
                    <div className="analytics__table-header">
                      <h2>Which venues are popular?</h2>
                      <button onClick={() => drillDown('venue_view')}>View All</button>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Venue</th>
                          <th>Views</th>
                          <th>Unique</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topVenues.map((v) => (
                          <tr key={v.venue_id} onClick={() => drillDown('venue_view', v.venue_id, v.venue_name)} style={{ cursor: 'pointer' }}>
                            <td>{v.venue_name}</td>
                            <td>{v.views}</td>
                            <td>{v.unique_user_count}</td>
                          </tr>
                        ))}
                        {topVenues.length === 0 && (
                          <tr><td colSpan={3} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)' }}>No data yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="analytics__table">
                    <div className="analytics__table-header">
                      <h2>What are people searching?</h2>
                      <button onClick={() => navigate(`/analytics/detail?days=${days}&event_type=search`)}>View All</button>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Search</th>
                          <th>Count</th>
                          <th>Users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topSearches.map((s) => (
                          <tr key={s.query} style={{ cursor: 'pointer' }} onClick={() => navigate(`/analytics/detail?days=${days}&event_type=search&query=${encodeURIComponent(s.query)}`)}>
                            <td>{s.query}</td>
                            <td>{s.count}</td>
                            <td>{s.unique_users}</td>
                          </tr>
                        ))}
                        {topSearches.length === 0 && (
                          <tr><td colSpan={3} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)' }}>No data yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Question 4: Are people converting? */}
                <div className="analytics__funnel">
                  <div className="analytics__funnel-card">
                    <h2>Are people joining guestlists?</h2>
                    <div className="analytics__funnel-row analytics__funnel-row--clickable" onClick={() => drillDown('join_guestlist_clicked')}>
                      <span className="label">Clicked "Join"</span>
                      <span className="value">{funnel?.guestlist?.clicks || 0}</span>
                    </div>
                    <div className="analytics__funnel-row analytics__funnel-row--clickable" onClick={() => drillDown('join_guestlist_clicked')}>
                      <span className="label">Actually joined</span>
                      <span className="value">{funnel?.guestlist?.actual_joins || 0}</span>
                    </div>
                    <div className="analytics__funnel-row">
                      <span className="label">Conversion</span>
                      <span className="rate">{guestlistRate}%</span>
                    </div>
                  </div>

                  <div className="analytics__funnel-card">
                    <h2>Are people booking tables?</h2>
                    <div className="analytics__funnel-row analytics__funnel-row--clickable" onClick={() => drillDown('view_tables_clicked')}>
                      <span className="label">Viewed tables</span>
                      <span className="value">{funnel?.bottle_service?.clicks || 0}</span>
                    </div>
                    <div className="analytics__funnel-row analytics__funnel-row--clickable" onClick={() => drillDown('bottle_purchase_started')}>
                      <span className="label">Completed purchase</span>
                      <span className="value">{funnel?.bottle_service?.actual_purchases || 0}</span>
                    </div>
                    <div className="analytics__funnel-row">
                      <span className="label">Conversion</span>
                      <span className="rate">{bottleRate}%</span>
                    </div>
                  </div>
                </div>

              </>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default Analytics;
